import { useAuth } from "src/context";
import { useDealer } from "./useDealer";
import { useSafeApiCall } from "./api";
import { useApiClient } from "./api/useApiClient";
import type {
  ApplicationCondition,
  NewConditionInput,
} from "src/types/conditions";
import { getConditionTaskID } from "src/utils/conditionUtils";

/**
 * Existing condition file with TaskID association
 */
interface ConditionFile {
  name: string;
  bytes: string; // base64 encoded
  task_id: string;
}

/**
 * New condition with associated file (not synced from DigiFi)
 */
interface NewConditionWithFile {
  title: string;
  internal_description?: string;
  external_description?: string;
  tag: string; // ConditionTag
  status: string; // TaskStatusType
  file_name: string;
  file_bytes: string; // base64 encoded
}

/**
 * Request body for uploading condition files to DigiFi.
 * Sends multiple condition files and/or new conditions in a single request.
 */
interface SendConditionFilesRequest {
  processed_by: string;
  application_id: string;
  los_id: string;
  files: ConditionFile[];
  new_conditions: NewConditionWithFile[];
}

/**
 * Response type for condition file upload
 */
interface SendConditionFilesResponse {
  success: boolean;
  error?: string;
}

/**
 * Converts a File object to base64 string for JSON transmission
 * @param file - The file to convert
 * @returns Promise resolving to base64 string
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        // Remove the "data:...;base64," prefix
        const base64 = result.split(",")[1];
        resolve(base64);
      } else {
        reject(new Error("Failed to read file as base64"));
      }
    };
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

export const useSendConditionFiles = () => {
  const { user } = useAuth();
  const { dealer_id } = useDealer();
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();

  /**
   * Sends condition files to DigiFi for upload.
   * Can include both existing conditions (with TaskIDs) and new conditions.
   *
   * @param conditionFiles - Map of condition ID to File (for existing conditions)
   * @param conditions - Array of conditions (used to look up TaskIDs)
   * @param applicationId - The application ID
   * @param newConditions - Array of new conditions to create (optional)
   * @param losId - The LOS ID from Digifi (required for Digifi requests)
   * @returns Promise resolving to success/error response
   * @throws Error if user email is unavailable or dealer ID is missing
   */
  const sendConditionFiles = async (
    conditionFiles: Map<string, File>,
    conditions: ApplicationCondition[],
    applicationId: string,
    newConditions: NewConditionInput[] = [],
    losId?: string,
  ): Promise<SendConditionFilesResponse> => {
    if (!user?.email) {
      throw new Error("User email not available");
    }

    if (!dealer_id) {
      throw new Error("Dealer ID not available");
    }

    if (conditionFiles.size === 0 && newConditions.length === 0) {
      throw new Error("No files or conditions to send");
    }

    try {
      // Build existing condition files request
      const files: Array<{
        name: string;
        bytes: string;
        task_id: string;
      }> = [];

      for (const [conditionId, file] of conditionFiles) {
        // Find the condition to get its TaskID
        const condition = conditions.find((c) => c.id === conditionId);
        const taskId = condition ? getConditionTaskID(condition) : undefined;

        if (!taskId) {
          throw new Error(
            `Task ID not found for condition ${conditionId}. Cannot upload file.`,
          );
        }

        // Convert file to base64
        const base64Content = await fileToBase64(file);

        files.push({
          name: file.name,
          bytes: base64Content,
          task_id: taskId,
        });
      }

      // Build new conditions request
      const newConditionsData: NewConditionWithFile[] = [];

      for (const newCondition of newConditions) {
        // Convert file to base64
        const base64Content = await fileToBase64(newCondition.file);

        newConditionsData.push({
          title: newCondition.title,
          internal_description: newCondition.internalDescription,
          external_description: newCondition.externalDescription,
          tag: newCondition.tag,
          status: newCondition.status,
          file_name: newCondition.file.name,
          file_bytes: base64Content,
        });
      }

      // Send to backend
      const requestBody: SendConditionFilesRequest = {
        processed_by: user.email,
        application_id: applicationId,
        los_id: losId || applicationId,
        files,
        new_conditions: newConditionsData,
      };

      await safeApiCall(() =>
        fetchFromApi(`/dealer-portal/v1/${dealer_id}/conditions`, {
          method: "POST",
          body: requestBody,
        }),
      );

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  return { sendConditionFiles };
};
